import React from 'react';
import classNames from 'classnames';
import { useEnvironment } from '@wix/yoshi-flow-editor';
import {
  LAYOUT_NAME_ONE_COLUMN_MOBILE,
  LAYOUT_NAME_SIDE_BY_SIDE_MOBILE,
  LAYOUT_NAME_TEXT_ON_IMAGE_MOBILE,
  type LayoutName,
  type MediaPlatformImage,
} from '@wix/communities-blog-client-common';
import { useFeedBorderRadius } from '../../hooks/use-feed-border-radius';
import { Image } from '../image/image';
import { useResponsiveContext } from '../responsive-listener/responsive-context';
import { useImageDimensions } from './hooks/use-image-dimensions';
import styles from './post-list-item-image.scss';

type PostListItemImageMobileProps = {
  image: MediaPlatformImage | string;
  url?: string;
  isPublic?: boolean;
  width: number;
  height: number;
  layoutName: LayoutName;
};

export const PostListItemImageMobile: React.FC<PostListItemImageMobileProps> = ({
  image,
  url,
  isPublic,
  width,
  height,
  layoutName,
}) => {
  const { rootWidth } = useResponsiveContext();
  const { dimensions: widgetDimensions } = useEnvironment();
  const { borderRadius } = useFeedBorderRadius();

  const dimensions = useImageDimensions({
    container: { width, height },
    component: { width: rootWidth ?? widgetDimensions.width },
    layoutName,
  });

  const imageWidth = typeof image === 'string' ? 1 : (image.width as number) || 1;
  const imageHeight = typeof image === 'string' ? 1 : (image.height as number) || 1;

  const aspectRatio = imageHeight / imageWidth;
  const targetWidth = dimensions.width;
  const targetHeight = dimensions.height || Math.floor(targetWidth * aspectRatio);

  const getBorderRadiusForLayout = () => {
    if (layoutName === LAYOUT_NAME_ONE_COLUMN_MOBILE) {
      return {
        borderTopRightRadius: borderRadius,
        borderTopLeftRadius: borderRadius,
      };
    } else if (
      layoutName === LAYOUT_NAME_SIDE_BY_SIDE_MOBILE ||
      layoutName === LAYOUT_NAME_TEXT_ON_IMAGE_MOBILE
    ) {
      return {
        borderRadius,
      };
    }

    return {};
  };

  const style = {
    aspectRatio:
      layoutName === LAYOUT_NAME_ONE_COLUMN_MOBILE
        ? ` ${imageWidth} / ${imageHeight}`
        : ` ${targetWidth} / ${targetHeight}`,
    ...getBorderRadiusForLayout(),
    overflow: 'hidden',
  };

  return (
    <div className={classNames(styles.container, styles.mobile)} style={style}>
      <Image
        width={width}
        height={height}
        socialAttrs={isPublic ? { 'data-pin-url': url } : { 'data-pin-nopin': true }}
        className={styles.imageFit}
        image={image}
      />
    </div>
  );
};

export default PostListItemImageMobile;
